import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { calendarAPI, holidayAPI, userAPI, oneDayAPI } from '../../services';
import { format, isSameDay, startOfMonth, endOfMonth, startOfWeek, addDays, addMonths, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';
import SimpleCalendar, { PublicHolidayInfo } from '../../components/SimpleCalendar';
import TeacherPageHeader from '../../components/TeacherPageHeader';
import { CalendarEvent, Holiday, User, OneDayRequest } from '../../types/api';
import { DEFAULT_TEACHER_ID } from '../../config/constants';
import { FaImage, FaCalendarPlus, FaCalendarDay, FaSchool } from 'react-icons/fa';
import { renderIcon } from '../../utils/iconRenderer';
import { Modal } from '../../components';
import MemberEditModal from '../../components/MemberEditModal';
import { formatPhoneNumber, formatBirthDate } from '../../utils/formatters';
import { getWeekdayLabel } from '../../utils/formatters/labels';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  TeacherSchedule,
  ScheduleFormData,
  EditFormData,
  RecurringPattern,
  WeekdayType,
  ConflictInfo
} from '../../types/schedule';
import {
  ScheduleForm,
  TodayScheduleSection,
  ScheduleDetailModal,
  ConflictModal,
  RescheduleModal,
  RescheduleData
} from '../../components/schedule';

interface LocationState {
  openModal?: boolean;
  isHoliday?: boolean;
}

const LessonSchedule: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;

  // 일정 데이터 상태
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedDateEvents, setSelectedDateEvents] = useState<CalendarEvent[]>([]);
  const [, setIsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // 공휴일 데이터 (SimpleCalendar에서 전달받음)
  const [publicHolidaysMap, setPublicHolidaysMap] = useState<Map<string, PublicHolidayInfo>>(new Map());

  // 폼 관련 상태
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<ScheduleFormData>({
    title: '',
    scheduleDate: '',
    startTime: '',
    endTime: '',
    description: '',
  });

  // 반복 일정 관련 상태
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringPattern, setRecurringPattern] = useState<RecurringPattern>('');
  const [recurringUntil, setRecurringUntil] = useState('');
  const [recurringWeekdays, setRecurringWeekdays] = useState<WeekdayType[]>([]);

  // 종일/휴일 관련 상태
  const [isAllDay, setIsAllDay] = useState(false);
  const [isHoliday, setIsHoliday] = useState(false);
  const [holidayEndDate, setHolidayEndDate] = useState('');
  const [editingHolidayId, setEditingHolidayId] = useState<number | null>(null);

  // 편집 관련 상태
  const [selectedSchedule, setSelectedSchedule] = useState<TeacherSchedule | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<TeacherSchedule | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({ title: '', scheduleDate: '', startTime: '', endTime: '', description: '' });
  const [editingOccurrenceDate, setEditingOccurrenceDate] = useState<string | null>(null);

  // 충돌 모달 상태
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictInfo, setConflictInfo] = useState<ConflictInfo | null>(null);

  // 회원 정보 모달 상태
  const [showMemberInfoModal, setShowMemberInfoModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const [isLoadingMember, setIsLoadingMember] = useState(false);

  // 회원 수정 모달 상태
  const [showMemberEditModal, setShowMemberEditModal] = useState(false);

  // 원데이클래스 상세 모달 상태
  const [showOneDayModal, setShowOneDayModal] = useState(false);
  const [selectedOneDay, setSelectedOneDay] = useState<OneDayRequest | null>(null);

  // 원데이클래스 등록 모달 상태
  const [showOneDayRegisterModal, setShowOneDayRegisterModal] = useState(false);
  const [oneDayForm, setOneDayForm] = useState({
    studentName: '',
    studentContact: '',
    scheduleDate: '',
    startTime: '',
    endTime: '',
  });

  // 일정 드래그 앤 드롭 상태
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const [dropTargetDate, setDropTargetDate] = useState<Date | null>(null);

  // SimpleCalendar에서 공휴일 데이터 전달받기
  const handlePublicHolidaysLoad = useCallback((holidaysMap: Map<string, PublicHolidayInfo>) => {
    setPublicHolidaysMap(holidaysMap);
  }, []);

  useEffect(() => {
    loadAllTeacherData();
    // 초기 로드 시 오늘 일정 설정
    const today = new Date();
    setSelectedDate(today);
  }, []);

  // calendarEvents가 변경되면 선택된 날짜의 이벤트 업데이트
  useEffect(() => {
    updateSelectedDateEvents(selectedDate);
  }, [calendarEvents]);

  // 대시보드에서 네비게이션으로 모달 열기
  useEffect(() => {
    if (locationState?.openModal) {
      setShowForm(true);
      setFormData({
        title: '',
        scheduleDate: format(new Date(), 'yyyy-MM-dd'),
        startTime: '',
        endTime: '',
        description: '',
      });
      if (locationState?.isHoliday) {
        setIsHoliday(true);
        setIsAllDay(true);
      }
      // state 초기화 (뒤로가기 시 다시 열리지 않도록)
      window.history.replaceState({}, document.title);
    }
  }, [locationState]);

  const updateSelectedDateEvents = (date: Date) => {
    const events = calendarEvents
      .filter(event => {
        const eventDate = new Date(event.start);
        return eventDate.toDateString() === date.toDateString();
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    setSelectedDateEvents(events);
  };

  const handleDateClick = (date: Date, dayEvents: CalendarEvent[]) => {
    setSelectedDate(date);
    setSelectedDateEvents(dayEvents.sort((a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime()
    ));
  };

  // 일정 타입별 색상 반환 (네이비 프로 시안 팔레트)
  const getEventColor = (scheduleType: string): string => {
    const colorMap: { [key: string]: string } = {
      'regular': '#1e56d6',
      'rescheduled': '#8b5cf6',
      'cancelled': '#b91c1c',
      'makeup': '#0f9d76',
      'oneday_class': '#e8930c',
      'personal': '#64748b',
      'meeting': '#334155',
      'break': '#94a3b8'
    };
    return colorMap[scheduleType] || '#1e56d6';
  };

  const loadAllTeacherData = async (targetDate?: Date) => {
    try {
      setIsLoading(true);
      const baseDate = targetDate || new Date();
      const startDate = format(startOfMonth(baseDate), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(baseDate), 'yyyy-MM-dd');
      const today = format(new Date(), 'yyyy-MM-dd');

      const effectiveStartDate = today < startDate ? today : startDate;
      const effectiveEndDate = today > endDate ? today : endDate;

      // 일정과 휴일 데이터 병렬 로드
      const [calendarResponse, holidayResponse] = await Promise.all([
        calendarAPI.getUnifiedCalendar({
          startDate: effectiveStartDate,
          endDate: effectiveEndDate,
          teacherId: DEFAULT_TEACHER_ID,
          includeRegular: true
        }),
        holidayAPI.getHolidays({
          year: baseDate.getFullYear(),
          month: baseDate.getMonth() + 1
        })
      ]);

      // 휴일 데이터 설정
      if (holidayResponse.data?.success && holidayResponse.data?.data) {
        setHolidays(holidayResponse.data.data);
      }

      const response = calendarResponse;

      if (response.data && response.data.success) {
        const eventsByDate = response.data.data.events || [];
        const allSchedules: any[] = [];

        eventsByDate.forEach((dayEvent: any) => {
          if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
            dayEvent.schedules.forEach((schedule: any) => {
              allSchedules.push({ ...schedule, date: dayEvent.date });
            });
          }
        });

        const events: CalendarEvent[] = allSchedules
          .filter((event: any) => event.scheduleType !== 'cancelled')
          .filter((event: any) => {
            const desc = event.description || '';
            // #exclude= 특정 날짜 제외
            const excludeMatch = desc.match(/#exclude=([^\s]+)/);
            if (excludeMatch) {
              const excludedDates = excludeMatch[1].split(',').map((s: string) => s.trim());
              if (excludedDates.includes(event.date)) return false;
            }
            // #exclude_from= 해당 날짜 이후 모두 제외
            const excludeFromMatch = desc.match(/#exclude_from=([^\s]+)/);
            if (excludeFromMatch) {
              if (event.date >= excludeFromMatch[1]) return false;
            }
            return true;
          })
          .map((event: any) => {
            // 종일(all-day) 일정은 시간이 null → 하루 전체로 배치 (crash 방지)
            const [startHour, startMinute] = (event.startTime || '00:00').split(':').map(Number);
            const [endHour, endMinute] = (event.endTime || '23:59').split(':').map(Number);
            const eventDate = new Date(event.date);

            return {
              id: `unified_${event.id}`,
              title: event.title || `${event.studentName} - 정규수업`,
              type: event.scheduleType === 'regular' ? 'lesson' : 'personal',
              start: new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), startHour, startMinute),
              end: new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), endHour, endMinute),
              color: getEventColor(event.scheduleType),
              data: { type: event.scheduleType, studentName: event.studentName, scheduleId: event.scheduleId, status: event.status, ...event }
            };
          });

        setCalendarEvents(events);
      }
    } catch {
      // Load failed silently
    } finally {
      setIsLoading(false);
    }
  };

  // 선택한 요일의 가장 가까운 다음 날짜 반환
  const getNextDateOfWeek = (weekday: WeekdayType): Date => {
    const map: Record<string, number> = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    const target = map[weekday];
    const today = new Date();
    const diff = (target - today.getDay() + 7) % 7 || 7;
    const next = new Date(today);
    next.setDate(today.getDate() + diff);
    return next;
  };

  // 시간 충돌 체크 함수
  const checkTimeConflict = (newSchedule: ScheduleFormData) => {
    const { scheduleDate, startTime, endTime } = newSchedule;
    const newStart = new Date(`${scheduleDate}T${startTime}`);
    const newEnd = new Date(`${scheduleDate}T${endTime}`);

    return calendarEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      if (!isSameDay(newStart, eventStart)) return false;
      return (newStart < eventEnd && newEnd > eventStart) || (eventStart < newEnd && eventEnd > newStart);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 휴일 등록인 경우
    if (isHoliday) {
      if (!formData.title || !formData.scheduleDate || !holidayEndDate) {
        alert('휴일 제목과 날짜를 입력해주세요.');
        return;
      }
      await createHoliday();
      return;
    }

    // 일반 일정 등록
    if (!formData.title || (!isRecurring && !formData.scheduleDate)) {
      alert('필수 항목을 입력해주세요.');
      return;
    }

    // 종일이 아닌 경우 시간 필수
    if (!isAllDay && (!formData.startTime || !formData.endTime)) {
      alert('시간을 입력해주세요.');
      return;
    }

    const conflictingEvents = (isRecurring || isAllDay) ? [] : checkTimeConflict(formData);
    if (conflictingEvents.length > 0) {
      setConflictInfo({ conflictingEvents, newSchedule: formData });
      setShowConflictModal(true);
      return;
    }

    await createSchedule(formData);
  };

  // 휴일 생성/수정
  const createHoliday = async () => {
    try {
      // 수정 모드인 경우 기존 휴일 먼저 삭제
      if (editingHolidayId) {
        await holidayAPI.deleteHoliday(editingHolidayId);
      }

      await holidayAPI.createHoliday({
        title: formData.title,
        startDate: formData.scheduleDate,
        endDate: holidayEndDate
      });

      const message = editingHolidayId
        ? '휴일이 수정되었습니다.'
        : '휴일이 등록되었습니다. 해당 기간에 수업이 있는 학생들에게 알림이 발송됩니다.';
      alert(message);
      handleFormCancel();
      loadAllTeacherData(currentMonth);
    } catch {
      alert('휴일 처리 중 오류가 발생했습니다.');
    }
  };

  const createSchedule = async (scheduleData: ScheduleFormData) => {
    try {
      const requestBase = {
        title: scheduleData.title,
        // 종일이면 시간 없이(null) 저장 → 종일 일정으로 표시됨
        startTime: isAllDay ? null : scheduleData.startTime,
        endTime: isAllDay ? null : scheduleData.endTime,
        isAllDay,
        description: scheduleData.description.trim() || undefined,
        scheduleType: 'personal' as const,
      };

      if (isRecurring && recurringPattern && recurringUntil && recurringWeekdays.length > 0) {
        // 선택된 요일들에 대해 각각 일정 생성
        for (const weekday of recurringWeekdays) {
          const baseDate = getNextDateOfWeek(weekday);
          await calendarAPI.createSchedule({
            ...requestBase,
            scheduleDate: format(baseDate, 'yyyy-MM-dd'),
            isRecurring: true,
            recurringPattern: recurringPattern as any,
            recurringUntil
          });
        }
      } else if (!isRecurring) {
        await calendarAPI.createSchedule({
          ...requestBase,
          scheduleDate: scheduleData.scheduleDate,
          isRecurring: false
        });
      }

      alert('일정이 등록되었습니다.');
      handleFormCancel();
      loadAllTeacherData(currentMonth);
    } catch {
      alert('일정 등록 중 오류가 발생했습니다.');
    }
  };

  const handleConfirmConflict = async () => {
    if (conflictInfo) {
      await createSchedule(conflictInfo.newSchedule);
      setShowConflictModal(false);
      setConflictInfo(null);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setFormData({ title: '', scheduleDate: '', startTime: '', endTime: '', description: '' });
    setIsRecurring(false);
    setRecurringPattern('');
    setRecurringUntil('');
    setRecurringWeekdays([]);
    setIsAllDay(false);
    setIsHoliday(false);
    setHolidayEndDate('');
    setEditingHolidayId(null); // 수정 모드 초기화
  };

  // 현재 시간 기준 반올림 (00/30) 시작시간 + 1시간 종료시간
  const getDefaultTimes = () => {
    const now = new Date();
    const min = now.getMinutes();
    let h = now.getHours();
    let m: number;
    if (min <= 0) { m = 0; }
    else if (min <= 30) { m = 30; }
    else { m = 0; h += 1; }
    const startTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const endH = h + 1;
    const endTime = `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    return { startTime, endTime };
  };

  const handleAddScheduleClick = () => {
    // 선택된 날짜로 폼 초기화
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const { startTime, endTime } = getDefaultTimes();
    setFormData({
      title: '',
      scheduleDate: dateStr,
      startTime,
      endTime,
      description: ''
    });
    setHolidayEndDate(dateStr);
    setShowForm(true);
  };

  const addExclusionToDescription = (description: string, dateStr: string): string => {
    const base = description || '';
    const idx = base.indexOf('#exclude=');
    if (idx === -1) return `${base}${base ? ' ' : ''}#exclude=${dateStr}`;
    const prefix = base.substring(0, idx);
    const rest = base.substring(idx + 9);
    const firstTokenEnd = rest.search(/[\s]/);
    const listStr = firstTokenEnd === -1 ? rest : rest.substring(0, firstTokenEnd);
    const tail = firstTokenEnd === -1 ? '' : rest.substring(firstTokenEnd);
    const parts = listStr.split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.includes(dateStr)) parts.push(dateStr);
    return `${prefix}#exclude=${parts.join(',')}${tail}`.trim();
  };

  const submitEdit = async () => {
    if (!editingSchedule) return;

    const changes = [];
    if (editForm.title !== editingSchedule.title) changes.push(`제목: "${editingSchedule.title}" → "${editForm.title}"`);
    if (editForm.scheduleDate !== editingSchedule.scheduleDate) {
      changes.push(`날짜: ${format(new Date(editingSchedule.scheduleDate), 'M/d (E)', { locale: ko })} → ${format(new Date(editForm.scheduleDate), 'M/d (E)', { locale: ko })}`);
    }
    if (editForm.startTime !== editingSchedule.startTime || editForm.endTime !== editingSchedule.endTime) {
      changes.push(`시간: ${editingSchedule.startTime.substring(0, 5)} - ${editingSchedule.endTime.substring(0, 5)} → ${editForm.startTime.substring(0, 5)} - ${editForm.endTime.substring(0, 5)}`);
    }
    if (editForm.description !== (editingSchedule.description || '')) changes.push(`설명 변경됨`);

    try {
      if (editingOccurrenceDate && editingSchedule.isRecurring) {
        await calendarAPI.createSchedule({
          title: editForm.title,
          scheduleDate: editingOccurrenceDate,
          startTime: editForm.startTime,
          endTime: editForm.endTime,
          description: editForm.description,
          scheduleType: 'personal',
        });
        const newDesc = addExclusionToDescription(editingSchedule.description || '', editingOccurrenceDate);
        await calendarAPI.updateSchedule(editingSchedule.scheduleId, { description: newDesc });
      } else {
        await calendarAPI.updateSchedule(editingSchedule.scheduleId, {
          title: editForm.title,
          scheduleDate: editForm.scheduleDate,
          startTime: editForm.startTime,
          endTime: editForm.endTime,
          description: editForm.description
        });
      }

      alert(changes.length > 0 ? `일정이 수정되었습니다.\n\n변경 내용:\n${changes.join('\n')}` : '일정이 저장되었습니다.');
      setSelectedSchedule(null);
      setEditingSchedule(null);
      setEditingOccurrenceDate(null);
      loadAllTeacherData(currentMonth);
    } catch {
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  const submitDelete = async () => {
    if (!editingSchedule) return;
    if (!window.confirm('이 일정을 삭제하시겠습니까?')) return;
    try {
      await calendarAPI.deleteSchedule(editingSchedule.scheduleId);
      setSelectedSchedule(null);
      setEditingSchedule(null);
      loadAllTeacherData(currentMonth);
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 일정 클릭 핸들러
  const handleScheduleClick = async (event: CalendarEvent) => {
    const data = event.data as Record<string, any>;
    const scheduleType = data?.scheduleType;

    if (scheduleType === 'oneday_class') {
      // 원데이클래스인 경우 상세 모달 표시
      if (data?.relatedId) {
        try {
          const response = await oneDayAPI.getRequests({ limit: 100 });
          if (response.success && response.data?.requests) {
            const oneDayRequest = response.data.requests.find(
              (r: OneDayRequest) => r.requestId === data.relatedId
            );
            if (oneDayRequest) {
              setSelectedOneDay(oneDayRequest);
              setShowOneDayModal(true);
            } else {
              alert('해당 원데이클래스 신청을 찾을 수 없습니다.');
            }
          }
        } catch {
          alert('원데이클래스 정보를 불러올 수 없습니다.');
        }
      }
    } else if (scheduleType === 'regular' && data?.userStringId) {
      // 정규 수업인 경우 회원 정보 모달 표시 (userStringId로 조회)
      setIsLoadingMember(true);
      try {
        const response = await userAPI.getUser(data.userStringId);
        if (response.success && response.data) {
          setSelectedMember(response.data);
          setShowMemberInfoModal(true);
        } else {
          alert('회원 정보를 찾을 수 없습니다.');
        }
      } catch {
        alert('회원 정보를 불러올 수 없습니다.');
      } finally {
        setIsLoadingMember(false);
      }
    } else if (scheduleType === 'personal' || scheduleType === 'meeting' || scheduleType === 'break') {
      // 개인일정: 기존 '일정 변경' 모달로 통일 (시간 변경·삭제 가능, 더블클릭과 동일)
      handleEventDoubleClick(event);
    }
  };

  // 회원 수정 모달 열기
  const handleOpenMemberEdit = () => {
    if (!selectedMember) return;
    setShowMemberInfoModal(false);
    setShowMemberEditModal(true);
  };

  // 회원 정보 모달 닫기
  const handleCloseMemberInfoModal = () => {
    setShowMemberInfoModal(false);
    setSelectedMember(null);
  };

  // 회원 수정 모달 닫기
  const handleCloseMemberEditModal = () => {
    setShowMemberEditModal(false);
    setSelectedMember(null);
  };

  // 원데이클래스 모달 닫기
  const handleCloseOneDayModal = () => {
    setShowOneDayModal(false);
    setSelectedOneDay(null);
  };

  // 드래그 앤 드롭으로 일정 변경
  const handleEventDrop = (event: CalendarEvent, newDate: Date) => {
    setDraggedEvent(event);
    setDropTargetDate(newDate);
    setShowRescheduleModal(true);
  };

  // 일정 변경 확인
  const handleConfirmReschedule = async (data: RescheduleData) => {
    if (!draggedEvent) return;

    const eventData = draggedEvent.data as Record<string, any>;
    const scheduleId = eventData?.scheduleId || eventData?.id;
    const scheduleType = eventData?.scheduleType;
    const isRegular = scheduleType === 'regular';

    if (!scheduleId) {
      alert('일정 ID를 찾을 수 없습니다.');
      return;
    }

    try {
      const newDateStr = format(data.newDate, 'yyyy-MM-dd');
      const originalDateStr = format(new Date(draggedEvent.start), 'yyyy-MM-dd');

      if (isRegular && !data.applyToFuture) {
        // 정규 수업의 임시 변경: 원본에 제외 날짜 추가 + 새 일정 생성
        const newDesc = addExclusionToDescription(eventData?.description || '', originalDateStr);

        // 원본 일정에 제외 날짜 추가
        await calendarAPI.updateSchedule(scheduleId, {
          description: newDesc
        });

        // 새 일정 생성 (변경된 날짜/시간으로)
        await calendarAPI.createSchedule({
          title: draggedEvent.title,
          scheduleDate: newDateStr,
          startTime: data.newStartTime + ':00',
          endTime: data.newEndTime + ':00',
          description: `[${originalDateStr} 수업 변경] ${eventData?.description || ''}`.trim(),
          scheduleType: 'rescheduled',
          userId: eventData?.userId,
          studentName: eventData?.studentName,
        });

        alert('이번 주 수업이 변경되었습니다.');
      } else if (isRegular && data.applyToFuture) {
        // 정규 수업 이후 변경: 원본에 #exclude_from 추가 + 새 반복 일정 생성
        const existingDesc = eventData?.description || '';
        // 기존 #exclude_from 제거 후 새로 추가
        const cleanDesc = existingDesc.replace(/#exclude_from=[^\s]+/g, '').trim();
        const newDesc = `${cleanDesc} #exclude_from=${originalDateStr}`.trim();

        // 원본 일정에 이후 제외 태그 추가
        await calendarAPI.updateSchedule(scheduleId, {
          description: newDesc,
        });

        // 새 반복 일정 생성 (변경된 날짜/시간으로)
        await calendarAPI.createSchedule({
          title: draggedEvent.title,
          scheduleDate: newDateStr,
          startTime: data.newStartTime + ':00',
          endTime: data.newEndTime + ':00',
          description: '',
          scheduleType: 'regular',
          userId: eventData?.userId,
          studentName: eventData?.studentName,
          isRecurring: true,
          recurringPattern: 'weekly',
          recurringUntil: eventData?.recurringUntil || format(new Date(new Date().getFullYear(), 11, 31), 'yyyy-MM-dd'),
        });

        alert('이후 모든 정규 수업 일정이 변경되었습니다.');
      } else {
        // 일반 일정 변경
        await calendarAPI.updateSchedule(scheduleId, {
          scheduleDate: newDateStr,
          startTime: data.newStartTime + ':00',
          endTime: data.newEndTime + ':00',
        });

        alert('일정이 변경되었습니다.');
      }

      setShowRescheduleModal(false);
      setDraggedEvent(null);
      setDropTargetDate(null);
      loadAllTeacherData(currentMonth);
    } catch {
      alert('일정 변경 중 오류가 발생했습니다.');
    }
  };

  // 라벨 더블클릭 → 일정 변경 모달
  const handleEventDoubleClick = (event: CalendarEvent) => {
    const eventDate = new Date(event.start);
    setDraggedEvent(event);
    setDropTargetDate(eventDate); // 같은 날짜로 설정 (시간만 변경 가능)
    setShowRescheduleModal(true);
  };

  // 일정 변경 취소
  const handleCancelReschedule = () => {
    setShowRescheduleModal(false);
    setDraggedEvent(null);
    setDropTargetDate(null);
  };

  // 일정 변경 모달에서 개인일정 삭제
  const handleRescheduleDelete = async () => {
    if (!draggedEvent) return;
    const data = draggedEvent.data as Record<string, any>;
    if (!data?.scheduleId) {
      alert('삭제할 일정 정보를 찾을 수 없습니다.');
      return;
    }
    if (!window.confirm('이 개인일정을 삭제하시겠습니까?')) return;
    try {
      await calendarAPI.deleteSchedule(data.scheduleId);
      setShowRescheduleModal(false);
      setDraggedEvent(null);
      setDropTargetDate(null);
      loadAllTeacherData(currentMonth);
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 이미지 내보내기 (주간 시간표 형식)
  const handleExportImage = async () => {
    // 선택된 날짜 기준 해당 주 (일~토)
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    // 해당 주의 이벤트 수집
    const weekEvents = weekDays.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      return calendarEvents
        .filter(e => format(new Date(e.start), 'yyyy-MM-dd') === dateStr)
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    });

    // 고정 시간 범위: 10시 ~ 22시
    const minHour = 10;
    const maxHour = 22;
    const hours = Array.from({ length: maxHour - minHour }, (_, i) => minHour + i);

    const ROW_H = 60;
    const COL_W = 120;
    const TIME_W = 50;
    const HEADER_H = 50;

    // 임시 컨테이너
    const container = document.createElement('div');
    container.style.cssText = `position:absolute;left:-9999px;top:0;background:#fff;padding:24px;width:${TIME_W + COL_W * 7 + 48}px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;`;
    document.body.appendChild(container);

    // 제목
    const title = document.createElement('div');
    title.style.cssText = 'text-align:center;margin-bottom:16px;';
    title.innerHTML = `<div style="font-size:20px;font-weight:700;color:#1e293b;">${format(weekStart, 'yyyy년 M월 d일')} ~ ${format(addDays(weekStart, 6), 'M월 d일')} 수업 시간표</div>`;
    container.appendChild(title);

    // 테이블 컨테이너
    const table = document.createElement('div');
    table.style.cssText = `position:relative;width:${TIME_W + COL_W * 7}px;`;

    // 요일 헤더
    const headerRow = document.createElement('div');
    headerRow.style.cssText = `display:flex;height:${HEADER_H}px;border-bottom:2px solid #e2e8f0;`;
    // 시간 열 빈칸
    const timeCorner = document.createElement('div');
    timeCorner.style.cssText = `width:${TIME_W}px;flex-shrink:0;`;
    headerRow.appendChild(timeCorner);

    const dayLabels = ['일', '월', '화', '수', '목', '금', '토'];
    weekDays.forEach((day, i) => {
      const col = document.createElement('div');
      const isSun = i === 0;
      const isSat = i === 6;
      const dateStr = format(day, 'yyyy-MM-dd');
      const isHol = publicHolidaysMap.get(dateStr)?.isPublicHoliday || holidays.some(h => dateStr >= h.startDate && dateStr <= h.endDate);
      const color = isSun || isHol ? '#dc2626' : isSat ? '#2563eb' : '#1e293b';
      col.style.cssText = `width:${COL_W}px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;justify-content:center;`;
      col.innerHTML = `<div style="font-size:13px;font-weight:600;color:${color};">${dayLabels[i]}</div><div style="font-size:12px;color:${color};">${format(day, 'M/d')}</div>`;
      headerRow.appendChild(col);
    });
    table.appendChild(headerRow);

    // 시간표 본체
    const body = document.createElement('div');
    body.style.cssText = `position:relative;height:${hours.length * ROW_H}px;`;

    // 시간 라벨 + 가로줄
    hours.forEach((h, idx) => {
      const row = document.createElement('div');
      row.style.cssText = `position:absolute;top:${idx * ROW_H}px;left:0;right:0;height:${ROW_H}px;border-bottom:1px solid #f1f5f9;display:flex;`;
      const timeLabel = document.createElement('div');
      timeLabel.style.cssText = `width:${TIME_W}px;flex-shrink:0;font-size:12px;color:#94a3b8;font-weight:500;padding-top:4px;text-align:right;padding-right:8px;`;
      timeLabel.textContent = `${h}:00`;
      row.appendChild(timeLabel);

      // 세로 구분선
      for (let c = 0; c < 7; c++) {
        const cell = document.createElement('div');
        cell.style.cssText = `width:${COL_W}px;flex-shrink:0;border-left:1px solid #f1f5f9;`;
        row.appendChild(cell);
      }
      body.appendChild(row);
    });

    // 이벤트 배치
    weekEvents.forEach((dayEvts, dayIdx) => {
      dayEvts.forEach(ev => {
        const evStart = new Date(ev.start);
        const evEnd = new Date(ev.end);
        const startMin = evStart.getHours() * 60 + evStart.getMinutes();
        const endMin = evEnd.getHours() * 60 + evEnd.getMinutes();
        const topPx = ((startMin - minHour * 60) / 60) * ROW_H;
        const heightPx = Math.max(((endMin - startMin) / 60) * ROW_H, 20);
        const leftPx = TIME_W + dayIdx * COL_W + 2;

        const evDiv = document.createElement('div');
        evDiv.style.cssText = `position:absolute;top:${topPx}px;left:${leftPx}px;width:${COL_W - 4}px;height:${heightPx - 2}px;background:${ev.color || '#4caf50'};color:#fff;border-radius:4px;padding:3px 6px;font-size:12px;overflow:hidden;box-sizing:border-box;line-height:1.3;`;
        evDiv.innerHTML = `<div style="font-weight:600;word-break:keep-all;">${ev.title}</div><div style="font-size:12px;opacity:0.9;">${format(evStart, 'HH:mm')}-${format(evEnd, 'HH:mm')}</div>`;
        body.appendChild(evDiv);
      });
    });

    table.appendChild(body);
    container.appendChild(table);

    try {
      const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      canvas.toBlob((blob) => {
        if (blob) {
          saveAs(blob, `시간표_${format(weekStart, 'yyyy-MM-dd')}.png`);
        }
      });
    } catch {
      alert('이미지 내보내기에 실패했습니다.');
    } finally {
      document.body.removeChild(container);
    }
  };

  // 엑셀 내보내기 (주간 시간표 형식) — 버튼 숨김 처리로 현재 미사용
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleExportExcel = () => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const dayLabels = ['일', '월', '화', '수', '목', '금', '토'];

    // 해당 주 이벤트 수집
    const weekEvents = weekDays.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      return calendarEvents
        .filter(e => format(new Date(e.start), 'yyyy-MM-dd') === dateStr)
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    });

    // 시간표 형식: 행=시간, 열=요일
    const rows: any[] = [];
    for (let h = 10; h < 22; h++) {
      const row: Record<string, string> = { '시간': `${h}:00` };
      weekDays.forEach((day, dayIdx) => {
        const colName = `${dayLabels[dayIdx]} (${format(day, 'M/d')})`;
        // 해당 시간대에 걸치는 이벤트 찾기
        const eventsInHour = weekEvents[dayIdx].filter(ev => {
          const startH = new Date(ev.start).getHours();
          const startM = new Date(ev.start).getMinutes();
          const endH = new Date(ev.end).getHours();
          const endM = new Date(ev.end).getMinutes();
          const evStartMin = startH * 60 + startM;
          const evEndMin = endH * 60 + endM;
          const slotStart = h * 60;
          const slotEnd = (h + 1) * 60;
          return evStartMin < slotEnd && evEndMin > slotStart;
        });
        row[colName] = eventsInHour
          .map(ev => `${ev.title} (${format(new Date(ev.start), 'HH:mm')}-${format(new Date(ev.end), 'HH:mm')})`)
          .join(' / ');
      });
      rows.push(row);
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    // 열 너비 설정
    ws['!cols'] = [{ wch: 6 }, ...weekDays.map(() => ({ wch: 25 }))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `시간표_${format(weekStart, 'MM-dd')}`);
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `시간표_${format(weekStart, 'yyyy-MM-dd')}.xlsx`);
  };

  // 원데이클래스 직접 등록
  const handleAddOneDayClass = () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const { startTime, endTime } = getDefaultTimes();
    setOneDayForm({
      studentName: '',
      studentContact: '',
      scheduleDate: dateStr,
      startTime,
      endTime,
    });
    setShowOneDayRegisterModal(true);
  };

  // 원데이클래스 등록 제출
  const handleSubmitOneDayClass = async () => {
    if (!oneDayForm.studentName || !oneDayForm.scheduleDate || !oneDayForm.startTime || !oneDayForm.endTime) {
      alert('이름, 날짜, 시간을 입력해주세요.');
      return;
    }

    try {
      await calendarAPI.createSchedule({
        title: `${oneDayForm.studentName} - 원데이클래스`,
        scheduleDate: oneDayForm.scheduleDate,
        startTime: oneDayForm.startTime + ':00',
        endTime: oneDayForm.endTime + ':00',
        scheduleType: 'oneday_class',
        studentName: oneDayForm.studentName,
        studentContact: oneDayForm.studentContact || undefined,
      });

      alert('원데이클래스가 등록되었습니다.');
      setShowOneDayRegisterModal(false);
      loadAllTeacherData(currentMonth);
    } catch {
      alert('원데이클래스 등록 중 오류가 발생했습니다.');
    }
  };

  // 휴일 등록
  const handleAddHoliday = () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    setFormData({ title: '', scheduleDate: dateStr, startTime: '', endTime: '', description: '' });
    setHolidayEndDate(dateStr);
    setIsHoliday(true);
    setIsAllDay(true);
    setShowForm(true);
  };

  // 헤더의 월 이동/오늘 버튼
  const handleHeaderMonthChange = (newDate: Date) => {
    setCurrentMonth(newDate);
    loadAllTeacherData(newDate);
  };

  return (
    <div className="teacher-page lesson-schedule-page">
      {/* 상단 헤더 바 (시안: 타이틀 + 월 이동 + 범례 + 수업 등록) */}
      <TeacherPageHeader title="수업일정관리">
        <div className="month-nav">
          <button
            type="button"
            className="month-nav-btn"
            aria-label="이전 달"
            onClick={() => handleHeaderMonthChange(subMonths(currentMonth, 1))}
          >
            ‹
          </button>
          <span className="month-label">{format(currentMonth, 'yyyy년 M월', { locale: ko })}</span>
          <button
            type="button"
            className="month-nav-btn"
            aria-label="다음 달"
            onClick={() => handleHeaderMonthChange(addMonths(currentMonth, 1))}
          >
            ›
          </button>
          <button type="button" className="today-btn" onClick={() => handleHeaderMonthChange(new Date())}>
            오늘
          </button>
        </div>
        <div className="calendar-legend">
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#1e56d6' }} />정규</span>
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#0f9d76' }} />보강</span>
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#8b5cf6' }} />변경</span>
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#e8930c' }} />원데이</span>
          <span className="legend-item"><span className="legend-swatch" style={{ background: '#64748b' }} />기타</span>
        </div>
      </TeacherPageHeader>

      {showForm && (
        <div className="schedule-form-modal-overlay" onClick={handleFormCancel}>
          <div className="schedule-form-modal" onClick={(e) => e.stopPropagation()}>
            <ScheduleForm
              formData={formData}
              setFormData={setFormData}
              isRecurring={isRecurring}
              setIsRecurring={setIsRecurring}
              recurringPattern={recurringPattern}
              setRecurringPattern={setRecurringPattern}
              recurringUntil={recurringUntil}
              setRecurringUntil={setRecurringUntil}
              recurringWeekdays={recurringWeekdays}
              setRecurringWeekdays={setRecurringWeekdays}
              isAllDay={isAllDay}
              setIsAllDay={setIsAllDay}
              isHoliday={isHoliday}
              setIsHoliday={setIsHoliday}
              holidayEndDate={holidayEndDate}
              setHolidayEndDate={setHolidayEndDate}
              onSubmit={handleSubmit}
              onCancel={handleFormCancel}
            />
          </div>
        </div>
      )}

      <div className="schedule-container">
        <div className="calendar-section">
          <SimpleCalendar
            hideHeader
            date={currentMonth}
            events={calendarEvents}
            holidays={holidays}
            onDateClick={handleDateClick}
            onMonthChange={(date) => {
              setCurrentMonth(date);
              loadAllTeacherData(date);
            }}
            selectedDate={selectedDate}
            onPublicHolidaysLoad={handlePublicHolidaysLoad}
            onEventDrop={handleEventDrop}
            onEventClick={handleScheduleClick}
            onEventDoubleClick={handleEventDoubleClick}
            draggableEventTypes={['regular', 'rescheduled', 'personal']}
          />
        </div>

        <div className="schedule-sidebar">
          {/* 액션 버튼 영역 */}
          <div className="sidebar-action-buttons">
            {/* 엑셀 내보내기 버튼 숨김 처리
            <button className="sidebar-action-btn" onClick={handleExportExcel} title="엑셀 내보내기">
              {renderIcon(FaFileExcel)}
              <span>엑셀</span>
            </button>
            */}
            <button className="sidebar-action-btn" onClick={handleExportImage} title="이미지 내보내기">
              {renderIcon(FaImage)}
              <span>이미지</span>
            </button>
            <button className="sidebar-action-btn" onClick={handleAddOneDayClass} title="원데이클래스 등록">
              {renderIcon(FaCalendarPlus)}
              <span>원데이</span>
            </button>
            <button className="sidebar-action-btn" onClick={handleAddScheduleClick} title="개인 일정 등록">
              {renderIcon(FaCalendarDay)}
              <span>개인일정</span>
            </button>
            {/* <button className="sidebar-action-btn" onClick={handleAddHoliday} title="학원 휴일 등록">
              {renderIcon(FaSchool)}
              <span>휴일</span>
            </button> */}
          </div>

          {/* 좌우 분할 영역 */}
          <div className="sidebar-split">
            {/* 왼쪽: 날짜별 일정 */}
            <div className="sidebar-left">
              <TodayScheduleSection
                selectedDate={selectedDate}
                selectedDateEvents={selectedDateEvents}
                onAddClick={handleAddScheduleClick}
                onScheduleClick={handleScheduleClick}
                onScheduleDoubleClick={handleEventDoubleClick}
                draggableEventTypes={['regular', 'rescheduled', 'personal']}
                academyHoliday={(() => {
                  const dateStr = format(selectedDate, 'yyyy-MM-dd');
                  return holidays.find(h => dateStr >= h.startDate && dateStr <= h.endDate) || null;
                })()}
                publicHolidayInfo={publicHolidaysMap.get(format(selectedDate, 'yyyy-MM-dd')) || null}
              />
            </div>
          </div>
        </div>
      </div>

      <ScheduleDetailModal
        schedule={selectedSchedule}
        editingSchedule={editingSchedule}
        editForm={editForm}
        setEditForm={setEditForm}
        onClose={() => setSelectedSchedule(null)}
        onSubmitEdit={submitEdit}
        onDelete={submitDelete}
      />

      <ConflictModal
        isOpen={showConflictModal}
        conflictInfo={conflictInfo}
        onConfirm={handleConfirmConflict}
        onClose={() => setShowConflictModal(false)}
      />

      {/* 회원 정보 모달 */}
      <Modal
        isOpen={showMemberInfoModal}
        onClose={handleCloseMemberInfoModal}
        title="회원 정보"
        size="sm"
      >
        {isLoadingMember ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>로딩 중...</div>
        ) : selectedMember && (
          <div className="member-info-modal">
            <div className="member-header">
              <div className="member-avatar">
                {selectedMember.name?.charAt(0)}
              </div>
              <div className="member-name-area">
                <div className="member-name">{selectedMember.name}</div>
                <div className="member-type">
                  {selectedMember.lessonType === '입시' ? '입시반' : '취미반'}
                </div>
              </div>
            </div>

            <div className="member-info-list">
              <div className="info-row">
                <span className="info-label">생년월일</span>
                <span className="info-value">
                  {formatBirthDate(selectedMember.birthDate) || '지정안됨'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">연락처</span>
                <span className="info-value">
                  {formatPhoneNumber(selectedMember.phoneNumber) || '지정안됨'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">수업시간</span>
                <span className="info-value highlight">
                  {selectedMember.lessonDayOfWeek && selectedMember.lessonStartTime && selectedMember.lessonEndTime
                    ? `${getWeekdayLabel(selectedMember.lessonDayOfWeek)} ${selectedMember.lessonStartTime.slice(0, 5)} - ${selectedMember.lessonEndTime.slice(0, 5)}`
                    : '지정안됨'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">수업료</span>
                <span className="info-value fee">
                  {selectedMember.monthlyFee
                    ? `${Number(selectedMember.monthlyFee).toLocaleString()}원`
                    : '지정안됨'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">납부일</span>
                <span className="info-value">
                  {selectedMember.paymentDayOfMonth
                    ? `매월 ${selectedMember.paymentDayOfMonth}일`
                    : '지정안됨'}
                </span>
              </div>
            </div>

            <div className="member-actions">
              <button className="btn btn-primary" onClick={() => {
                handleCloseMemberInfoModal();
                if (selectedMember) {
                  const event = calendarEvents.find(e => {
                    const d = e.data as Record<string, any>;
                    return d?.userStringId === selectedMember.userId;
                  });
                  if (event) handleEventDoubleClick(event);
                }
              }}>
                일정 변경
              </button>
              <button className="btn btn-primary" onClick={() => {
                handleCloseMemberInfoModal();
                navigate('/admin/lesson-log', { state: { selectedStudentId: selectedMember?.userId } });
              }}>
                수업 기록
              </button>
              <button className="btn btn-secondary" onClick={handleOpenMemberEdit}>
                정보 수정
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* 회원 수정 모달 */}
      <MemberEditModal
        isOpen={showMemberEditModal}
        onClose={handleCloseMemberEditModal}
        member={selectedMember}
        onSaveSuccess={() => {
          loadAllTeacherData(currentMonth);
          window.dispatchEvent(new Event('refreshPendingCounts'));
        }}
      />

      {/* 원데이클래스 상세 모달 */}
      <Modal
        isOpen={showOneDayModal}
        onClose={handleCloseOneDayModal}
        title="원데이클래스 신청 상세"
        size="sm"
      >
        {selectedOneDay && (
          <div className="detail-content compact">
            <div className="detail-header">
              <div className="name-row">
                <span className="student-name">{selectedOneDay.name}</span>
                <span className={`status-badge ${selectedOneDay.status}`}>
                  {selectedOneDay.status === 'pending' ? '대기중' :
                    selectedOneDay.status === 'contacted' ? '연락완료' :
                      selectedOneDay.status === 'scheduled' ? '일정확정' :
                        selectedOneDay.status === 'completed' ? '수업완료' :
                          selectedOneDay.status === 'cancelled' ? '취소됨' : selectedOneDay.status}
                </span>
              </div>
              <div className="meta-info">
                {formatBirthDate(selectedOneDay.birthDate)}
              </div>
            </div>

            <div className="detail-info-list">
              <div className="info-row">
                <span className="label">연락처</span>
                <span className="value">{formatPhoneNumber(selectedOneDay.phoneNumber)}</span>
              </div>
              {selectedOneDay.email && (
                <div className="info-row">
                  <span className="label">이메일</span>
                  <span className="value">{selectedOneDay.email}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">희망시간</span>
                <span className="value">{selectedOneDay.preferredTime}</span>
              </div>
              {selectedOneDay.message && (
                <div className="info-row">
                  <span className="label">문의</span>
                  <span className="value">{selectedOneDay.message}</span>
                </div>
              )}
              {selectedOneDay.scheduledAt && (
                <div className="info-row">
                  <span className="label">{selectedOneDay.status === 'completed' ? '수업일' : '예정일'}</span>
                  <span className="value">
                    {format(new Date(selectedOneDay.scheduledAt), 'yyyy년 M월 d일 HH:mm', { locale: ko })}
                  </span>
                </div>
              )}
              <div className="info-row">
                <span className="label">신청일</span>
                <span className="value">{formatBirthDate(selectedOneDay.createdAt)}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 원데이클래스 등록 모달 */}
      <Modal
        isOpen={showOneDayRegisterModal}
        onClose={() => setShowOneDayRegisterModal(false)}
        title="원데이클래스 등록"
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              학생 이름 <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={oneDayForm.studentName}
              onChange={(e) => setOneDayForm({ ...oneDayForm, studentName: e.target.value })}
              placeholder="학생 이름"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              연락처
            </label>
            <input
              type="tel"
              value={oneDayForm.studentContact}
              onChange={(e) => setOneDayForm({ ...oneDayForm, studentContact: formatPhoneNumber(e.target.value) })}
              placeholder="010-0000-0000"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
              수업 날짜 <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="date"
              value={oneDayForm.scheduleDate}
              onChange={(e) => setOneDayForm({ ...oneDayForm, scheduleDate: e.target.value })}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                시작 시간 <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="time"
                value={oneDayForm.startTime}
                onChange={(e) => setOneDayForm({ ...oneDayForm, startTime: e.target.value })}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                종료 시간 <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="time"
                value={oneDayForm.endTime}
                onChange={(e) => setOneDayForm({ ...oneDayForm, endTime: e.target.value })}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <button
              className="btn btn-primary"
              style={{ flex: 1 }}
              onClick={handleSubmitOneDayClass}
            >
              등록
            </button>
            <button
              className="btn btn-secondary"
              style={{ flex: 1 }}
              onClick={() => setShowOneDayRegisterModal(false)}
            >
              취소
            </button>
          </div>
        </div>
      </Modal>

      {/* 일정 변경 확인 모달 */}
      <RescheduleModal
        isOpen={showRescheduleModal}
        onClose={handleCancelReschedule}
        onConfirm={handleConfirmReschedule}
        onDelete={handleRescheduleDelete}
        event={draggedEvent}
        newDate={dropTargetDate}
      />
    </div>
  );
};

export default LessonSchedule;
